/**
 * Vote forecast (screenshots/27-vote-forecast-model.png, 26-legislation-detail.png).
 *
 * "How likely is this member to vote the way this lobbying client wants?"
 * Four signals, as specified by product, every forecast starts from the member's record on similar items:
 *   1. Money-linked votes: past roll calls where the client filed a position AND the member
 *      received money from the client (or its registered lobbyists) in the 12 months before.
 *   2. Leaning: the member's leaning score vs. the bill's leaning score.
 *   3. Council tension: how split the council has been on this topic.
 * Plus, when the evidence exists, more factors the model learns weights for (model.ts): sponsorship of the item,
 * the committee's own action, what the member said on the record, mayor-sponsored items, allies' declared
 * stances, the ward's resident voice, money amounts from clients with a stated position, and the relationship graph.
 *
 * The hand-tuned math below is the prior. A learned ForecastModel (model.ts, trained nightly by train.ts from
 * decided roll calls and our own frozen forecasts) puts a weight on each factor's log-odds; with PRIOR_MODEL
 * the result is exactly the hand model, so forecastVote(i) and forecastVote(i, PRIOR_MODEL) agree bit for bit.
 *
 * Pure and deterministic: the Worker computes forecasts for the KV snapshot / Durable Object
 * broadcast, and the client recomputes the "why" breakdown from the same inputs.
 * Money near a vote is not evidence that it influenced the vote; the UI always says so.
 */
import { BASE_FACTORS, type FactorId, type ForecastModel, isPrior, NEW_FACTORS, type NewFactorId, PRIOR_MODEL, topicOfInput } from "./model";

export type { FactorId, ForecastModel, NewFactorId };
export type Position = "support" | "oppose";
export type ForecastLabel = "likely" | "tossup" | "unlikely";

export interface ForecastInput {
  /** The client's (or viewer's) position on the bill. All probabilities are "votes this way". */
  position: Position;
  /** Member's yes rate on the most similar past items (0..1) and how many items that is. */
  similar: { yesRate: number; n: number };
  /** Roll calls this client had a position on, after giving the member money: k went the client's way, of n. */
  moneyLinked: { k: number; n: number };
  /** Leaning scores, -1 progressive … +1 conservative (People tab estimate; bill = sponsors + similar votes). */
  memberLean: number;
  billLean: number;
  /** 0 = members vote together on this topic, 1 = deeply split. See councilTension(). */
  tension: number;
  /** Council-wide yes rate on similar items (the "usual outcome"). */
  councilYesRate: number;

  // ---- Evidence for the learned factors (model.ts). All optional; absent or null = the factor contributes nothing.
  //      Every value is in the YES frame (what it says about a yes vote); forecastVote() flips it for "oppose".
  /** The member's ward (1–50), for the model's per-member effects. */
  ward?: number;
  /** The member sponsors the item (lead) or co-sponsors it. */
  sponsor?: "lead" | "co" | null;
  /** The committee's recorded action before the Council vote: rec +1 recommended to pass, −1 do not pass / failed;
   *  margin = |yeas − nays| / (yeas + nays) (0.5 without a tally); member = this member sits on that committee. */
  committee?: { rec: 1 | -1 | 0; margin: number; member: boolean; nayShare?: number | null } | null;
  /** The member's latest on-the-record stance on this item (INTERNAL appearances, dated before the forecast). */
  said?: "support" | "oppose" | null;
  /** Recency-weighted stance on the item's topic over the last 12 months, −1 (opposed) … +1 (supported). */
  saidTopic?: number | null;
  /** Mayor-sponsored item: the member's yes record on earlier divided mayor-sponsored roll calls vs the council's. */
  mayor?: { sponsored: boolean; k: number; n: number; councilRate: number } | null;
  /** Agreement-weighted declared stance of the member's closest colleagues, −1 … +1. */
  allies?: number | null;
  /** The ward's verified "How would you vote?" totals (totals only, never per person). */
  wardVoice?: { support: number; oppose: number } | null;
  /** Signed log-dollars from clients with a STATED position, 12 months before (positive = from supporters). */
  moneyAmount?: number | null;
  /** Relationship-graph proximity to the client (cosine of node embeddings) × the client's position. Live only. */
  graph?: number | null;
  /** The item is about this member's own ward (title, geocoded address or TIF district: scripts/fetch_public.py wards). */
  homeWard?: "own" | null;
  /** The item's procedural history before the vote: deferred and published, substituted, re-referred, days in committee. */
  procedure?: { deferred: boolean; substituted: boolean; reReferred: boolean; daysInCommittee: number | null } | null;
  /** The ward's electorate on a Mayor-sponsored item: Brandon Johnson's 2023 runoff share in the ward (data/elections_2023.json). */
  wardElectorate?: { johnsonShare: number | null; mayorSponsored: boolean } | null;
  /** A ward demographic chosen for the item's topic (renter share for housing, income for budget), as a z-score across wards (ACS). */
  wardDemographics?: { value: number | null; variable?: string } | null;
  /** The Mayor spoke about this item on the record (YouTube channel, before the date): how many videos, and the member's
   *  record on earlier divided Mayor-sponsored items (k of n; council rate c), so the signal points each member their own way. */
  mayorStatement?: { videos: number; k: number; n: number; councilRate: number } | null;
  /** The council's second vote axis (people/leaning.ts voteAxis score2): the member's and the bill's positions on it. */
  memberLean2?: number | null;
  billLean2?: number | null;
  /** The item's topic (worker/src/tension.js topicOf: housing | budget | licenses | other), for topic-specific weights. */
  topic?: string | null;
  /** The member's chance of being absent for this vote (recent absence rate, shrunk); used by the tally projection only. */
  absence?: number | null;
}

export interface ForecastFactor {
  id: FactorId;
  /** Signed effect in log-odds; positive pushes toward the position. Drives the factor bars. */
  effect: number;
}

export interface Forecast {
  p: number;            // 0..1, probability the member votes `position`
  low: number;          // range band
  high: number;
  label: ForecastLabel;
  confidence: "low" | "medium" | "high";
  factors: ForecastFactor[];
}

/** Prior strength: money-linked history needs ~4 votes before it outweighs the member's similar-vote record. */
export const PRIOR = 4;
export const LIKELY = 0.65;
export const UNLIKELY = 0.35;
/** Ward voice counts only once this many verified residents answered (legislation/logic.ts MIN_WARD_VOICE). */
export const MIN_WARD_VOICE = 25;

const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));
const logit = (p: number) => { const q = clamp(p, 0.03, 0.97); return Math.log(q / (1 - q)); };
const sigmoid = (z: number) => 1 / (1 + Math.exp(-z));
/** Flip a yes-probability into "votes this position". */
const toward = (position: Position, yes: number) => (position === "support" ? yes : 1 - yes);
const num = (v: number | null | undefined) => (typeof v === "number" && Number.isFinite(v) ? v : 0);

/** The yes-frame log-odds each piece of new evidence contributes before any learned weight (model.ts). */
export function evidenceEffects(i: ForecastInput): Record<NewFactorId, number> {
  const c = i.committee;
  const m = i.mayor;
  const cr = m ? clamp(m.councilRate, 0.05, 0.95) : 0.5;
  const wv = i.wardVoice, pr = i.procedure, we = i.wardElectorate, ms = i.mayorStatement;
  const msc = ms ? clamp(num(ms.councilRate) || 0.5, 0.05, 0.95) : 0.5;
  return {
    sponsor: i.sponsor === "lead" ? 2.0 : i.sponsor === "co" ? 1.2 : 0,
    committee: (c && c.rec ? c.rec * (0.3 + (c.member ? 0.5 + 0.7 * clamp(num(c.margin), 0, 1) : 0)) : 0) - 1.5 * clamp(num(c?.nayShare), 0, 1),
    said: (i.said === "support" ? 1.5 : i.said === "oppose" ? -1.5 : 0) + 0.8 * clamp(num(i.saidTopic), -1, 1),
    mayor: m?.sponsored && m.n >= 0 ? logit((Math.max(0, m.k) + 4 * cr) / (Math.max(0, m.n) + 4)) - logit(cr) : 0,
    allies: clamp(num(i.allies), -1, 1),
    wardVoice: wv && wv.support + wv.oppose >= MIN_WARD_VOICE ? (wv.support - wv.oppose) / (wv.support + wv.oppose) : 0,
    moneyAmount: clamp(num(i.moneyAmount), -3, 3),
    graph: clamp(num(i.graph), -1, 1),
    homeWard: i.homeWard === "own" ? 2.0 : 0,
    procedure: pr ? -clamp(0.8 * Number(!!pr.deferred) + 0.5 * Number(!!pr.substituted) + 0.4 * Number(!!pr.reReferred) + 0.3 * Math.log1p(Math.max(0, num(pr.daysInCommittee)) / 180), 0, 3) : 0,
    wardElectorate: we?.mayorSponsored && we.johnsonShare != null ? clamp((we.johnsonShare - 0.5) * 4, -2, 2) : 0,
    wardDemographics: clamp(num(i.wardDemographics?.value), -1, 1),
    mayorStatement: ms && ms.videos > 0 ? Math.min(1, Math.log1p(ms.videos)) * (2 * ((Math.max(0, ms.k) + 4 * msc) / (Math.max(0, ms.n) + 4)) - 1) * 2 : 0,
    // Second axis: like the leaning factor (closer to where the yes side of similar items sits → likelier yes), prior weight 0.
    leaning2: typeof i.memberLean2 === "number" && typeof i.billLean2 === "number" ? logit(clamp(1 - Math.abs(i.memberLean2 - i.billLean2), 0.05, 0.95)) : 0,
  };
}

/**
 * Every factor's raw log-odds in the POSITION frame: the four original factors exactly as the hand model computes
 * them, and each piece of new evidence (yes frame × sign of the position). Also the hand forecast itself.
 */
export function rawEffects(i: ForecastInput) {
  const T = clamp(i.tension, 0, 1);
  const n = Math.max(0, i.moneyLinked.n);
  const k = clamp(i.moneyLinked.k, 0, n);
  const sign: 1 | -1 = i.position === "support" ? 1 : -1;

  // 1. Starting point: the member's record on similar items.
  const s = toward(i.position, i.similar.yesRate);
  // 2. Money-linked history, shrunk toward that record when there are few votes.
  const h = (k + PRIOR * s) / (n + PRIOR);
  // 3. Leaning: the closer the member is to the bill, the likelier a yes.
  const l = toward(i.position, clamp(1 - Math.abs(i.memberLean - i.billLean), 0.05, 0.95));
  // 4. Council tension: low = members follow the council's usual outcome;
  //    high = members split by leaning, and the range widens.
  const c = toward(i.position, i.councilYesRate);

  const wH = 0.45 * (n / (n + PRIOR));
  const wL = 0.1 + 0.4 * T;
  const wS = Math.max(0.05, 1 - wH - wL);
  const lambda = 0.5 * (1 - T);

  const eS = wS * logit(s);
  const eH = wH * logit(h);
  const eL = wL * logit(l);
  const pMember = sigmoid(eS + eH + eL);
  const pHand = clamp((1 - lambda) * pMember + lambda * c, 0.01, 0.99);
  const eT = logit(pHand) - (eS + eH + eL);
  const rHand = clamp(0.04 + 0.1 * T + 0.18 / Math.sqrt(n + i.similar.n / 2 + 1), 0.04, 0.35);

  const ev = evidenceEffects(i);
  const raw: Record<FactorId, number> = {
    similar: eS, moneyLinked: eH, leaning: eL, tension: eT,
    sponsor: sign * ev.sponsor, committee: sign * ev.committee, said: sign * ev.said, mayor: sign * ev.mayor,
    allies: sign * ev.allies, wardVoice: sign * ev.wardVoice, moneyAmount: sign * ev.moneyAmount, graph: sign * ev.graph,
    homeWard: sign * ev.homeWard, procedure: sign * ev.procedure, wardElectorate: sign * ev.wardElectorate, wardDemographics: sign * ev.wardDemographics,
    mayorStatement: sign * ev.mayorStatement, leaning2: sign * ev.leaning2,
  };
  return { raw, T, n, sign, pHand, rHand };
}

const finish = (p: number, r: number, factors: ForecastFactor[]): Forecast => ({
  p,
  low: clamp(p - r, 0.01, 0.99),
  high: clamp(p + r, 0.01, 0.99),
  label: p >= LIKELY ? "likely" : p <= UNLIKELY ? "unlikely" : "tossup",
  confidence: r < 0.12 ? "high" : r < 0.17 ? "medium" : "low",
  factors,
});

/** The factors to show: the four original ones always (in the Why panel's order), new ones only with evidence. */
const factorList = (raw: Record<FactorId, number>, effect: (id: FactorId) => number): ForecastFactor[] => [
  ...BASE_FACTORS.map((id): ForecastFactor => ({ id, effect: effect(id) })),
  ...NEW_FACTORS.filter((id) => raw[id] !== 0).map((id): ForecastFactor => ({ id, effect: effect(id) })),
];

export function forecastVote(i: ForecastInput, model: ForecastModel = PRIOR_MODEL): Forecast {
  const { raw, T, sign, pHand, rHand } = rawEffects(i);
  if (isPrior(model)) {
    // The hand model, exactly as before: new evidence is listed (weight 0) but moves nothing.
    return finish(pHand, rHand, factorList(raw, (id) => ((BASE_FACTORS as FactorId[]).includes(id) ? raw[id] : 0)));
  }
  const tw = model.topicWeights?.[topicOfInput(i.topic)];
  const w = (id: FactorId) => (model.weights[id] ?? 0) + (model.tensionSlopes[id] ?? 0) * T + (tw?.[id] ?? 0);
  const baseline = sign * (model.intercept + (i.ward != null ? model.members[i.ward] ?? 0 : 0));
  const effect = (id: FactorId) => w(id) * raw[id] + (id === "tension" ? baseline : 0);
  const factors = factorList(raw, effect);
  const z = factors.reduce((a, f) => a + f.effect, 0);
  const p = clamp(sigmoid(z), 0.01, 0.99);
  const pYes = i.position === "support" ? p : 1 - p;
  const extra = model.rangeBins ? model.rangeBins[Math.min(9, Math.floor(pYes * 10))] || 0 : 0;
  const r = clamp(model.rangeScale * rHand + extra, 0.04, 0.35);
  return finish(p, r, factors);
}

/**
 * Council tension for a topic, 0..1, from recent roll calls on it (last 12 months).
 * Mean closeness of the split (1 = 25–25, 0 = unanimous), weighted by how many were divided at all.
 */
export function councilTension(rollCalls: { yea: number; nay: number }[]): number {
  const votes = rollCalls.filter((v) => v.yea + v.nay > 0);
  if (!votes.length) return 0;
  const closeness = votes.map((v) => 1 - Math.abs(v.yea - v.nay) / (v.yea + v.nay));
  const divided = votes.filter((v) => v.nay > 0 && v.yea > 0).length / votes.length;
  const mean = closeness.reduce((a, b) => a + b, 0) / votes.length;
  return Math.round(clamp(Math.sqrt(mean * divided), 0, 1) * 100) / 100;
}

export const tensionLabel = (t: number) => (t >= 0.5 ? "High" : t >= 0.25 ? "Medium" : "Low");

/**
 * Projected roll call from per-member yes-probabilities (position = "support"). `pAbsent` (optional, per member) is the
 * chance of not voting: an absent member is neither a yes nor a no, so expected yeas shrink by it and `absent` is reported.
 */
export function projectRollCall(pYes: number[], needed: number, pAbsent?: (number | null | undefined)[]) {
  const a = pYes.map((_, i) => clamp(pAbsent?.[i] ?? 0, 0, 0.9));
  const q = pYes.map((p, i) => p * (1 - a[i]));
  const expected = q.reduce((s, x) => s + x, 0);
  const expectedAbsent = a.reduce((s, x) => s + x, 0);
  const sd = Math.sqrt(q.reduce((s, x) => s + x * (1 - x), 0));
  const likelyYes = pYes.filter((p) => p >= LIKELY).length;
  const likelyNo = pYes.filter((p) => p <= UNLIKELY).length;
  const yes = Math.round(expected), absent = Math.round(expectedAbsent);
  return {
    yes,
    no: Math.max(0, pYes.length - yes - absent),
    absent,
    range: [Math.max(0, Math.round(expected - 1.64 * sd)), Math.min(pYes.length, Math.round(expected + 1.64 * sd))] as [number, number],
    likelyYes, tossup: pYes.length - likelyYes - likelyNo, likelyNo,
    passes: yes >= needed,
  };
}

/** Under 3 similar past votes there is no forecast, ever (screenshots/54 state 1): show NotEnoughHistory instead of the meter. */
export const MIN_SIMILAR_VOTES = 3;
export const enoughHistory = (i: Pick<ForecastInput, "similar">) => i.similar.n >= MIN_SIMILAR_VOTES;

/** The evidence fields of an input (or a producer's member row): what travels with forecast-inputs besides the four factors. */
export const EVIDENCE_KEYS = ["ward", "sponsor", "committee", "said", "saidTopic", "mayor", "allies", "wardVoice", "moneyAmount", "graph", "homeWard", "procedure", "wardElectorate", "wardDemographics", "mayorStatement", "memberLean2", "billLean2", "topic", "absence"] as const;
export type Evidence = Pick<ForecastInput, (typeof EVIDENCE_KEYS)[number]>;
export function evidenceOf(x: Partial<ForecastInput> | null | undefined): Evidence {
  const out: Record<string, unknown> = {};
  for (const k of EVIDENCE_KEYS) if (x && x[k] != null) out[k] = x[k];
  return out as Evidence;
}

/** The same input seen from the "support" side: what every learned parameter is expressed in. */
export function yesFrame(i: ForecastInput): ForecastInput {
  if (i.position === "support") return i;
  const n = Math.max(0, i.moneyLinked.n), k = clamp(i.moneyLinked.k, 0, n);
  return { ...i, position: "support", moneyLinked: { k: n - k, n } };
}
